import React, { useState, useEffect } from "react";
import { db } from "../firebase"; // Adjust path to your Firebase config
import {
  collection,
  onSnapshot,
  doc,
  runTransaction,
  increment,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  collectionGroup,
  orderBy
} from "firebase/firestore";
import { FaWallet, FaUserCircle, FaPlus, FaMinus, FaCoins, FaHistory, FaCog } from "react-icons/fa";
import "./Wallet.css";

const Wallet = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Effect 1: Listen for and display wallet data for the UI ---
  useEffect(() => {
    const walletsRef = collection(db, "customer_wallets");
    const q = query(walletsRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const walletData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWallets(walletData);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching wallets:", err);
      setError("Failed to load wallet data.");
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- Effect 2: Efficiently listen for NEW subscriptions to auto-process them ---
  useEffect(() => {
    const subscriptionsQuery = query(collectionGroup(db, 'user_subscriptions'));

    const unsubscribe = onSnapshot(subscriptionsQuery, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "added") {
          const subscription = { id: change.doc.id, ...change.doc.data() };
          const subscriptionRef = change.doc.ref;
          
          if (subscription.status === 'active' && !subscription.walletCreated) {
            await processNewSubscription(subscription, subscriptionRef);
          }
        }
      });
    });
    return () => unsubscribe();
  }, []);

  // --- Core Logic: Process a new subscription ---
  const processNewSubscription = async (subscription, subscriptionRef) => {
    const { customerEmail, customerName, customerId, packageId, packageName } = subscription;

    if (!customerId || !packageId) {
        console.error(`Subscription ${subscription.id} is missing 'customerId' or 'packageId'. Halting.`);
        return;
    }

    // --- Step 1: Get the number of coins for this specific package ---
    const packageRef = doc(db, "packages", packageId);
    const packageSnap = await getDoc(packageRef);
    if (!packageSnap.exists()) {
        console.error(`Package with ID ${packageId} not found. Cannot process subscription.`);
        return;
    }
    const coinsFromPackage = packageSnap.data().walletCoins || 0;
    
    // --- Step 2: Find the customer's wallet ---
    const walletsRef = collection(db, "customer_wallets");
    const q = query(walletsRef, where("customerId", "==", customerId));
    
    try {
      const existingWalletSnapshot = await getDocs(q);

      if (existingWalletSnapshot.empty) {
        // --- Case A: Wallet does NOT exist. Create one with the initial coins. ---
        console.log(`INFO: No wallet for customerId [${customerId}]. Creating new wallet with ${coinsFromPackage} coins.`);

        const counterRef = doc(db, "counters", "wallet_counter");
        await runTransaction(db, async (transaction) => {
          const counterDoc = await transaction.get(counterRef);
          if (!counterDoc.exists()) throw new Error("CRITICAL: Counter document is missing!");
          
          const newIdNumber = counterDoc.data().current_id + 1;
          const newWalletId = `W-${String(newIdNumber).padStart(3, '0')}`;
          transaction.update(counterRef, { current_id: newIdNumber });

          const newWalletRef = doc(db, "customer_wallets", newWalletId);
          transaction.set(newWalletRef, {
            customerName, customerEmail, customerId,
            walletCoins: coinsFromPackage,
            createdAt: serverTimestamp(),
            createdBy: "subscription",
            transactions: [{
              type: "credit", amount: coinsFromPackage,
              description: `Initial coins from package: ${packageName}`,
              timestamp: new Date(), source: "subscription"
            }]
          });
          
          transaction.update(subscriptionRef, { walletCreated: true, walletId: newWalletId });
        });

      } else {
        // =================================================================================
        // START OF THE FIX: Logic for existing wallets - ALWAYS ADD COINS
        // =================================================================================
        
        // --- Case B: Wallet EXISTS. Add coins from the new package to the existing balance. ---
        const existingWalletDoc = existingWalletSnapshot.docs[0];
        console.log(`INFO: Found existing wallet ${existingWalletDoc.id}. Adding ${coinsFromPackage} coins from new package.`);
        
        await runTransaction(db, async (transaction) => {
            // Atomically increment the coin balance in the wallet
            transaction.update(existingWalletDoc.ref, {
                walletCoins: increment(coinsFromPackage),
                transactions: [
                    ...(existingWalletDoc.data().transactions || []),
                    {
                        type: "credit",
                        amount: coinsFromPackage,
                        description: `Coins added from new package: ${packageName}`,
                        timestamp: new Date(),
                        source: "subscription_renewal" // Different source for clarity
                    }
                ]
            });

            // Mark this new subscription as processed
            transaction.update(subscriptionRef, { 
                walletCreated: true, 
                walletId: existingWalletDoc.id 
            });
        });
        console.log(`SUCCESS: Added ${coinsFromPackage} coins to wallet ${existingWalletDoc.id}.`);
        
        // =================================================================================
        // END OF THE FIX
        // =================================================================================
      }
    } catch (e) {
        console.error(`Transaction failed for subscription ${subscription.id}: `, e);
        setError("Failed to process a subscription. Check console for details.");
    }
  };

  // --- Manual Coin Management (For Admins) ---
  const handleUpdateCoins = async (walletId, amount) => {
    const walletRef = doc(db, "customer_wallets", walletId);
    try {
      const description = `Manual ${amount > 0 ? 'credit' : 'debit'} of coins by admin`;
      await runTransaction(db, async (transaction) => {
        const walletDoc = await transaction.get(walletRef);
        if (!walletDoc.exists()) throw new Error("Wallet document not found.");

        transaction.update(walletRef, {
            walletCoins: increment(amount),
            transactions: [
                ...(walletDoc.data().transactions || []),
                {
                    type: amount > 0 ? "credit" : "debit",
                    amount: Math.abs(amount),
                    description: description,
                    timestamp: new Date(),
                    source: "manual_admin"
                }
            ]
        });
      });
    } catch (err) {
      console.error(`Error updating coins for wallet ${walletId}:`, err);
      setError("Failed to update coin balance.");
    }
  };

  // --- Render Logic ---
  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }
  if (error) {
    return <div className="error-container">{error}</div>;
  }
  
  const totalCoins = wallets.reduce((sum, wallet) => sum + (wallet.walletCoins || 0), 0);

  return (
    <div className="wallet-container">
        <div className="wallet-header">
            <FaWallet className="wallet-icon" />
            <h1>Customer Wallets</h1>
            <div className="status-indicator">
                <FaCog className="cog-icon" />
                <span>Auto-processing new subscriptions</span>
            </div>
        </div>

        <div className="stats-grid">
            <div className="stat-card">
                <FaWallet className="stat-icon" />
                <div className="stat-content">
                    <h3>{wallets.length}</h3>
                    <p>Total Wallets</p>
                </div>
            </div>
            <div className="stat-card">
                <FaCoins className="stat-icon" />
                <div className="stat-content">
                    <h3>{totalCoins.toLocaleString()}</h3>
                    <p>Total Coins in Circulation</p>
                </div>
            </div>
        </div>

        <div className="wallet-table-container">
            <div className="table-header">
                <h3><FaHistory /> Wallet Details</h3>
            </div>
            <table className="wallet-table">
            <thead>
                <tr>
                <th>Wallet ID</th>
                <th>Customer</th>
                <th>Coins</th>
                <th>Source</th>
                <th>Created On</th>
                <th>Manual Actions</th>
                </tr>
            </thead>
            <tbody>
                {wallets.length === 0 ? (
                <tr>
                    <td colSpan="6" className="no-data">
                    No wallets found. Wallets are created automatically on a customer's first subscription.
                    </td>
                </tr>
                ) : (
                wallets.map((wallet) => (
                    <tr key={wallet.id}>
                    <td className="wallet-id-cell">{wallet.id}</td>
                    <td>
                        <div className="customer-cell">
                        <FaUserCircle />
                        <span>{wallet.customerName}</span>
                        </div>
                    </td>
                    <td className="coins-cell">
                        <span>{wallet.walletCoins?.toLocaleString() || 0}</span>
                    </td>
                    <td className="source-cell">
                        <span className={`source-badge ${wallet.createdBy}`}>
                        {wallet.createdBy === 'subscription' ? 'Auto-Created' : 'Manual'}
                        </span>
                    </td>
                    <td className="date-cell">
                        {wallet.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="actions-cell">
                        <button
                        className="coin-button add"
                        onClick={() => handleUpdateCoins(wallet.id, 100)}
                        title="Add 100 coins"
                        >
                        <FaPlus /> 100
                        </button>
                        <button
                        className="coin-button remove"
                        onClick={() => handleUpdateCoins(wallet.id, -50)}
                        title="Remove 50 coins"
                        >
                        <FaMinus /> 50
                        </button>
                    </td>
                    </tr>
                ))
                )}
            </tbody>
            </table>
        </div>
    </div>
  );
};

export default Wallet;